import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine
} from 'recharts';
import { useGetEliteFlowQuery } from 'store/services/admin/analyticsApi';
import { AnalyticsPeriod, EliteFlowDataPoint } from 'types/interfaces/admin/analytics';
import TimeRangeDropdown from './TimeRangeDropdown';

interface Props {
  period: AnalyticsPeriod;
  onPeriodChange: (period: AnalyticsPeriod) => void;
}

interface ChartRow {
  date: string;
  signups: number;
  cancellations: number; // negative for diverging display
  cancellationsAbs: number; // positive for tooltip
}

const SIGNUP_COLOR = '#22C55E';
const CANCEL_COLOR = '#EF4444';

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

interface TooltipPayload {
  payload: ChartRow;
}

const FlowTooltip: React.FC<{ active?: boolean; payload?: TooltipPayload[]; label?: string }> = ({
  active,
  payload,
  label
}) => {
  if (!active || !payload || !payload.length || !label) return null;
  const row = payload[0].payload;
  return (
    <div
      style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #2a2a2a',
        borderRadius: '8px',
        color: '#fff',
        padding: '8px 12px',
        fontSize: 13
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{formatDate(label)}</div>
      <div style={{ color: SIGNUP_COLOR }}>
        {row.signups.toLocaleString()} signups
      </div>
      <div style={{ color: CANCEL_COLOR }}>
        {row.cancellationsAbs.toLocaleString()} cancellations
      </div>
    </div>
  );
};

const EliteSignupsCancellationsChart: React.FC<Props> = ({ period, onPeriodChange }) => {
  const { data, isLoading, error } = useGetEliteFlowQuery({ period });

  const chartData: ChartRow[] = useMemo(() => {
    if (!data?.data) return [];
    return data.data.map((row: EliteFlowDataPoint) => ({
      date: row.date,
      signups: row.signups,
      cancellations: -row.cancellations,
      cancellationsAbs: row.cancellations
    }));
  }, [data]);

  const totals = useMemo(() => {
    if (!data?.data) return { signups: 0, cancellations: 0 };
    return data.data.reduce(
      (acc, row) => ({
        signups: acc.signups + row.signups,
        cancellations: acc.cancellations + row.cancellations
      }),
      { signups: 0, cancellations: 0 }
    );
  }, [data]);

  return (
    <div className="chart-card">
      <div className="chart-header">
        <div className="chart-header-left">
          <h2>Elite Signups vs Cancellations</h2>
          <p className="chart-subtitle">Daily flow into and out of paid Elite</p>
        </div>
        <div className="chart-header-right">
          <div className="chart-legend-multi">
            <div className="chart-legend">
              <span className="legend-dot elite-signups" />
              <span className="legend-label">Signups</span>
            </div>
            <div className="chart-legend">
              <span className="legend-dot elite-cancellations" />
              <span className="legend-label">Cancellations</span>
            </div>
          </div>
          <TimeRangeDropdown value={period} onChange={onPeriodChange} />
        </div>
      </div>

      <div className="chart-container">
        {isLoading ? (
          <div className="chart-loading">Loading...</div>
        ) : error ? (
          <div className="chart-error">Failed to load data</div>
        ) : data ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }} stackOffset="sign">
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                stroke="#666"
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#2a2a2a' }}
                tickLine={false}
                interval={chartData.length > 60 ? Math.floor(chartData.length / 12) : 0}
              />
              <YAxis
                stroke="#666"
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#2a2a2a' }}
                tickLine={false}
                tickFormatter={(value) => Math.abs(value).toLocaleString()}
                allowDecimals={false}
              />
              <Tooltip content={<FlowTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
              <ReferenceLine y={0} stroke="#666" />
              <Bar
                dataKey="signups"
                fill={SIGNUP_COLOR}
                radius={[4, 4, 0, 0]}
                name="Signups"
              />
              <Bar
                dataKey="cancellations"
                fill={CANCEL_COLOR}
                radius={[0, 0, 4, 4]}
                name="Cancellations"
              />
            </BarChart>
          </ResponsiveContainer>
        ) : null}
      </div>

      {data && (
        <div className="chart-summary">
          Last {period === 7 ? 'week' : period === 30 ? 'month' : 'year'}: {totals.signups.toLocaleString()} signups, {totals.cancellations.toLocaleString()} cancellations (net {(totals.signups - totals.cancellations >= 0 ? '+' : '')}{(totals.signups - totals.cancellations).toLocaleString()})
        </div>
      )}
    </div>
  );
};

export default EliteSignupsCancellationsChart;
